import { MONTHS } from 'app-constants';
import { useModalProvider } from 'context/ModalProviderContext';
import { getDocs, QueryDocumentSnapshot, Timestamp } from 'firebase/firestore';
import SelectUsersModal from 'modals/SelectUsers/SelectUsers';
import React from 'react';
import toast from 'react-hot-toast';
import { FilterItem, getQuery } from 'services';
import { Button, Splash } from 'shared/Components';
import FilterControle from 'shared/Components/FlexebleTable/Components/FilterControle';
import styled from 'styled-components';

import { Box, FlexContainer, Image, Text } from '../../../../shared/Typhography';

const OrderingCarrot = styled.span<{ direction: 'asc' | 'desc' }>`
  display: inline-block;
  transform: rotate(${({ direction }) => (direction == 'asc' ? '90deg' : '-90deg')});
`;

const Container = styled.div`
  margin-top: 150px;
`;

interface RevenueArtistItem {
  artist: number;
  company: number;
  total: number;
  id: string;
  name: string;
  profileUrl: string;
  share: number;
}

interface RevenueBase {
  id?: string;
  artist: number;
  company: number;
  total: number;
  date: Timestamp;
  artists: RevenueArtistItem[];
}

const dateParse = (date?: Date) => {
  if (!date) return 'Not Set';

  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

export default function RevenueAnalytics() {
  const { openModal, closeModal } = useModalProvider();

  const [filters, setFilters] = React.useState<{
    firebase: FilterItem[];
    fields: { [key: string]: any };
  }>({ firebase: [], fields: {} });
  const [ready, setReady] = React.useState<boolean>(false);
  const [data, setData] = React.useState<RevenueBase[]>([]);
  const [isLoading, setLoading] = React.useState<boolean>(true);
  const [fetchArtists, setFetchArtists] = React.useState<string[]>();
  const [sortedTotals, setSortedTotals] = React.useState<RevenueArtistItem[]>([]);
  const [sortFields, setSortFields] = React.useState<
    { field: 'artist' | 'company' | 'total' | 'name' | 'share'; type: 'asc' | 'desc' } | undefined
  >({ field: 'total', type: 'desc' });

  const [total, setTotal] = React.useState<{
    artist: number;
    total: number;
    perArtist: {
      [key: string]: RevenueArtistItem;
    };
  }>({ artist: 0, total: 0, perArtist: {} });

  const calculateTotal = () => {
    const tTotal: {
      artist: number;
      total: number;
      perArtist: {
        [key: string]: RevenueArtistItem;
      };
    } = {
      artist: 0,
      total: 0,
      perArtist: {},
    };

    data.forEach((revenueItem) => {
      tTotal.artist += revenueItem?.artist;
      tTotal.total += revenueItem?.total;

      revenueItem.artists.forEach((artistRevenue) => {
        if (artistRevenue?.id in tTotal.perArtist) {
          if (artistRevenue.artist)
            tTotal.perArtist[artistRevenue?.id].artist += artistRevenue.artist;
          if (artistRevenue.total) tTotal.perArtist[artistRevenue?.id].total += artistRevenue.total;
        } else {
          tTotal.perArtist[artistRevenue?.id] = {
            ...artistRevenue,
            artist: Number.isNaN(artistRevenue?.artist) ? 0 : artistRevenue?.artist,
            total: Number.isNaN(artistRevenue?.total) ? 0 : artistRevenue?.total,
          };
        }
      });
    });

    setTotal(tTotal);
  };

  const load = async () => {
    setLoading(true);
    setTotal({ artist: 0, perArtist: {}, total: 0 });

    try {
      const nextQuery = getQuery('revenue', filters.firebase, []);

      const nextData = await getDocs(nextQuery);
      const docs = nextData?.docs as QueryDocumentSnapshot<RevenueBase>[];
      const tData = [];
      for (let i = 0; i < docs?.length; i++) {
        const revenueItem = {
          id: nextData?.docs[i]?.id,
          ...(nextData?.docs[i]?.data() as RevenueBase),
          artists: [],
        } as RevenueBase;

        const artistQuery = getQuery(`revenue/${nextData?.docs[i]?.id}/artists`, [], []);
        const artistDocs = await getDocs(artistQuery);
        revenueItem.artists = artistDocs?.docs?.map((doc) => doc?.data() as RevenueArtistItem);
        tData.push(revenueItem);
      }
      setData(tData);
    } catch (err) {
      toast.error('Failed to fetch');
      console.log(err);
    }
    setLoading(false);
  };

  const manageOrdering = (attribute: 'artist' | 'company' | 'total' | 'name' | 'share') => {
    if (sortFields) {
      if (sortFields?.field === attribute) {
        if (sortFields.type === 'asc') setSortFields({ field: attribute, type: 'desc' });
        else setSortFields(undefined);
      }
    } else setSortFields({ field: attribute, type: 'asc' });
  };

  React.useEffect(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);

    const artistsQuery = params.get('artists');
    if (artistsQuery) setFetchArtists(JSON.parse(artistsQuery));

    const filtersQuery = params.get('filters');
    if (filtersQuery) {
      const tFilters: { firebase: FilterItem[]; fields: { [key: string]: any } } = {
        firebase: [],
        fields: {},
      };
      const filtersValue = JSON.parse(filtersQuery);

      if ('date' in filtersValue) {
        tFilters.firebase.push({
          value: Timestamp.fromDate(new Date(filtersValue?.date?.startDate)),
          attribute: 'date',
          operator: '>=',
        });

        tFilters.firebase.push({
          value: Timestamp.fromDate(new Date(filtersValue?.date?.endDate)),
          attribute: 'date',
          operator: '<=',
        });

        tFilters.fields = {
          ...tFilters.fields,
          date: {
            startDate: new Date(filtersValue?.date?.startDate),
            endDate: new Date(filtersValue?.date?.endDate),
          },
        };
      }
      setFilters(tFilters);
    }

    setReady(true);
  }, []);

  React.useEffect(() => {
    if (ready) load();
  }, [filters, ready]);

  React.useEffect(() => {
    if (!total?.perArtist) return;

    const tSortedData: RevenueArtistItem[] = [];
    for (const key in total?.perArtist) tSortedData.push(total?.perArtist[key]);

    if (sortFields)
      setSortedTotals(
        tSortedData.sort((item1, item2) => {
          let compare1 = item1?.[sortFields?.field] as any;
          let compare2 = item2?.[sortFields?.field] as any;

          if (typeof item1?.[sortFields?.field] === 'string') {
            compare1 = (item1?.[sortFields?.field] as string)
              .split('')
              .map((i) => i.charCodeAt(0))
              .reduce((p, c) => p + c, 0);
            compare2 = (item1?.[sortFields?.field] as string)
              .split('')
              .map((i) => i.charCodeAt(0))
              .reduce((p, c) => p + c, 0);
          }

          return sortFields?.type === 'asc' ? compare1 - compare2 : compare2 - compare1;
        }),
      );
    else setSortedTotals(tSortedData);
  }, [total, sortFields]);

  React.useEffect(() => {
    calculateTotal();
  }, [data]);

  return (
    <Container>
      <FlexContainer flexDirection="row" flexWrap="wrap" padding="0 64px" gap="10px ">
        <Box style={{ flex: 1 }}>
          <FlexContainer
            padding="10px"
            flexDirection="row"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
          >
            <div style={{ flex: 1 }} key="filters">
              <FilterControle
                loading={isLoading}
                onChange={(item) => {
                  setFilters({
                    firebase: [
                      ...filters.firebase.filter((filter) => filter?.attribute !== item?.name),
                      { attribute: item?.name, value: item?.value, operator: '==' },
                    ],
                    fields: {
                      ...filters.fields,
                      [item.name]: item,
                    },
                  });
                }}
                clear={(name: string) => {
                  setFilters({
                    firebase: filters.firebase.filter((item: any) => item?.attribute !== name),
                    fields: {
                      ...filters.fields,
                      [name]: undefined,
                    },
                  });
                }}
                values={filters.fields}
                keys={[
                  {
                    name: 'date',
                    placeholder: 'Date Range',
                    options: [],
                    type: 'date-range',
                    onChange: (dates: [Date | null, Date | null]) => {
                      const start = dates['0'];
                      const end = dates['1'];
                      if (start) {
                        start.setUTCHours(0);
                        start.setUTCMinutes(0);
                        start.setUTCSeconds(0);
                      }

                      if (end) {
                        end.setUTCHours(23);
                        end.setUTCMinutes(59);
                        end.setUTCSeconds(59);
                      }

                      if (!start || !end) {
                        if (filters.firebase.find((fil) => fil.attribute === 'date')) {
                          setFilters({
                            firebase: filters.firebase.filter((fil) => fil.attribute !== 'date'),
                            fields: {
                              ...filters.fields,
                              date: undefined,
                            },
                          });
                        }
                        return;
                      }

                      let firebaseFilter: FilterItem[] = filters.firebase;
                      if (start && end) {
                        firebaseFilter.push({
                          value: Timestamp.fromDate(start),
                          attribute: 'date',
                          operator: '>=',
                        });

                        firebaseFilter.push({
                          value: Timestamp.fromDate(end),
                          attribute: 'date',
                          operator: '<=',
                        });
                      } else {
                        firebaseFilter = firebaseFilter.filter(
                          (item) => item?.attribute !== 'date',
                        );
                      }

                      setFilters({
                        firebase: [...firebaseFilter],
                        fields: {
                          ...filters.fields,
                          date: { startDate: start, endDate: end },
                        },
                      });
                    },
                  },
                ]}
              />
            </div>
            <div key="buttons">
              <Button
                width="128"
                height="50"
                onClick={() =>
                  openModal('select-artists', SelectUsersModal as React.FC, {
                    selectedArtists: fetchArtists,
                    setArtists: setFetchArtists,
                    close: () => {
                      closeModal('select-artists');
                    },
                  })
                }
              >
                <Text size={1.1} weight="bold" color="white" cursor="pointer">
                  {fetchArtists?.length ? (
                    <>{fetchArtists?.length} selected artists</>
                  ) : (
                    <>Select Artists</>
                  )}
                </Text>
              </Button>
            </div>
          </FlexContainer>
        </Box>
        <Box style={{ flex: 1 }}>
          <div style={{ padding: '10px' }}>
            <FlexContainer
              flexDirection="row"
              alignItems="center"
              style={{ height: '64px', padding: '0 10px' }}
            >
              <span style={{ flex: 1 }}>Date Range</span>
              <span style={{ flex: 1 }}>Artist Royalty</span>
              <span style={{ flex: 1 }}>Total</span>
            </FlexContainer>
            <FlexContainer
              flexDirection="row"
              alignItems="center"
              style={{ height: '64px', padding: '0 10px' }}
            >
              {total && !isLoading && (
                <>
                  <span style={{ flex: 1 }}>
                    {filters?.fields?.date?.startDate &&
                      filters?.fields?.date?.endDate &&
                      `${dateParse(filters.fields.date.startDate)} - ${dateParse(
                        filters.fields.date.endDate,
                      )}`}
                    {(!filters?.fields?.date?.startDate || !filters?.fields?.date?.endDate) &&
                      'Unset'}
                  </span>
                  <span style={{ flex: 1 }}>
                    ${' '}
                    {fetchArtists?.length
                      ? fetchArtists
                          .map((item) => total?.perArtist?.[item])
                          .reduce((p, c) => p + (c?.artist ?? 0), 0)
                          ?.toFixed(5)
                      : total.artist?.toFixed(5)}
                  </span>
                  <span style={{ flex: 1 }}>
                    ${' '}
                    {fetchArtists?.length
                      ? fetchArtists
                          .map((item) => total?.perArtist?.[item])
                          .reduce((p, c) => p + (c?.total ?? 0), 0)
                          ?.toFixed(5)
                      : total.total?.toFixed(5)}
                  </span>
                </>
              )}
              {(!total || isLoading) && <span style={{ flex: 1 }}>Loading...</span>}
            </FlexContainer>
          </div>
        </Box>
      </FlexContainer>
      <FlexContainer
        margin="10px 0 0 0"
        flexDirection="row"
        padding="0 64px"
        alignItems="center"
        justifyContent="center"
      >
        <Box style={{ flex: 1 }}>
          {!isLoading && (
            <>
              <FlexContainer flexDirection="row">
                <span style={{ flex: 1, fontWeight: 600, userSelect: 'none' }}>Artist Profile</span>
                <span
                  style={{ flex: 1, fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => manageOrdering('name')}
                >
                  Artist Name
                  {sortFields && sortFields.field === 'name' && (
                    <OrderingCarrot direction={sortFields.type}>&#8227;</OrderingCarrot>
                  )}
                </span>
                <span
                  style={{ flex: 1, fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => manageOrdering('share')}
                >
                  Artist Share
                  {sortFields && sortFields.field === 'share' && (
                    <OrderingCarrot direction={sortFields?.type}>&#8227;</OrderingCarrot>
                  )}
                </span>
                <span
                  style={{ flex: 1, fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => manageOrdering('artist')}
                >
                  Artist Royalty
                  {sortFields && sortFields.field === 'artist' && (
                    <OrderingCarrot direction={sortFields?.type}>&#8227;</OrderingCarrot>
                  )}
                </span>
                <span
                  style={{ flex: 1, fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => manageOrdering('total')}
                >
                  Total Royalty
                  {sortFields && sortFields.field === 'total' && (
                    <OrderingCarrot direction={sortFields?.type}>&#8227;</OrderingCarrot>
                  )}
                </span>
              </FlexContainer>
              {sortedTotals.map((sortedItem) => {
                if (fetchArtists?.length && !fetchArtists?.includes(sortedItem?.id)) return <></>;

                return (
                  <FlexContainer flexDirection="row" alignItems="center" key={sortedItem?.id}>
                    <span style={{ flex: 1 }}>
                      <Image
                        borderRadiusType="circle"
                        width="64px"
                        height="64px"
                        src={sortedItem?.profileUrl}
                        style={{ background: 'gray' }}
                      />
                    </span>
                    <span style={{ flex: 1 }}> {sortedItem?.name}</span>
                    <span style={{ flex: 1 }}> {sortedItem?.share * 100} %</span>
                    <span style={{ flex: 1 }}>$ {(sortedItem?.artist ?? 0)?.toFixed(5)}</span>
                    <span style={{ flex: 1 }}>$ {(sortedItem?.total ?? 0)?.toFixed(5)}</span>
                  </FlexContainer>
                );
              })}
            </>
          )}
          {isLoading && <Splash position="relative" />}
        </Box>
      </FlexContainer>
    </Container>
  );
}
